import { useState, useEffect, useRef } from 'react';
import { clientService, ClientData, ClientListResponse } from '../services/clientService';
import { getUserFriendlyErrorMessage } from '../utils/errorUtils';

interface UseClientsParams {
  type?: string;
  projectId?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  search?: string;
}

interface UseClientsReturn {
  clients: ClientData[];
  loading: boolean;
  error: string | null;
  pagination: ClientListResponse['pagination'] | null;
  refetch: () => void;
}

export const useClients = (params: UseClientsParams = {}): UseClientsReturn => {
  const [clients, setClients] = useState<ClientData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<ClientListResponse['pagination'] | null>(null);
  // Tracks the most recent request so slow/out-of-order responses from older
  // filter/page/search changes can't overwrite the latest result.
  const latestRequestId = useRef(0);

  const fetchClients = async () => {
    const requestId = ++latestRequestId.current;
    try {
      setLoading(true);
      setError(null);
      const response = await clientService.getClients(params);
      if (requestId !== latestRequestId.current) return; // a newer request superseded this one
      setClients(response.data);
      setPagination(response.pagination);
    } catch (err) {
      if (requestId !== latestRequestId.current) return;
      console.error('Error fetching clients:', err);
      const humanizedError = getUserFriendlyErrorMessage(err, 'Failed to fetch clients');
      setError(humanizedError);
      setClients([]);
      setPagination(null);
    } finally {
      if (requestId === latestRequestId.current) setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, [
    params.type,
    params.projectId,
    params.page,
    params.limit,
    params.sortBy,
    params.sortOrder,
    params.search
  ]);

  return {
    clients,
    loading,
    error,
    pagination,
    refetch: fetchClients
  };
};

interface UseClientParams {
  clientId: string | number;
  enabled?: boolean;
}

interface UseClientReturn {
  client: ClientData | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export const useClient = ({ clientId, enabled = true }: UseClientParams): UseClientReturn => {
  const [client, setClient] = useState<ClientData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchClient = async () => {
    if (!enabled || !clientId) return;
    
    try {
      setLoading(true);
      setError(null);
      const response = await clientService.getClientById(clientId);
      setClient(response);
    } catch (err) {
      console.error('Error fetching client:', err);
      const humanizedError = getUserFriendlyErrorMessage(err, 'Failed to fetch client');
      setError(humanizedError);
      setClient(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClient();
  }, [clientId, enabled]);

  return {
    client,
    loading,
    error,
    refetch: fetchClient
  };
}; 